import { useLayoutEffect, useState } from "react";
import ShipmentDistribution from "@components/templates/Profile/ShipmentDistribution";
import { getEventName, getUserActivePlan } from "@helpers/profileHelper.js";
import { useParams, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";
import styles from "./PortPairs.module.scss";
import { trademoTrackingEvents } from "@core/global/trademo-tracking";
import { generateTrackingObject } from "@core/global/global-country-functions";
import { profileTypeEnum, profileTypeEndPoint } from "@components/templates/Profile/ProfileHeader/profile-enum";
import { profileCategory, tradeLaneEvents } from "@containers/TradeLanesProfile/utils/enum";
import LanesChart from "@core/charts/LanesChart";
import { getTradeLanesChartData } from "@containers/TradeLanesProfile/utils/helpers";
import { profileMapDataHandler, tradeLaneCategoryHandler, tradeLaneCachingHandler } from "@redux/TradeLanesProfile/tradeLanesProfileSlice";
import NoSearchDataComponent from "@shared/country-components/no-search-data";

const PortPairs = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [mapData, setMapData] = useState([]);
  const [imageData, setImageData] = useState([]);
  const [shipmentDistribution, setShipmentDistribution] = useState([]);
  const [shipmentDistributionLoader, setShipmentDistributionLoader] = useState(true);
  const { tradeLaneId, tradeLaneName } = useParams();
  const { search: searchUrlParams, pathname } = useLocation();
  const dataSource = new URLSearchParams(searchUrlParams).get("dataSource");

  const dispatch = useDispatch();
  const {
    tradeLaneCategory,
    tabDetails: { activeTabId },
    profileRedirectionId,
    tradeLaneCaching,
  } = useSelector((state) => state.tradeLanesProfile);

  const fetchMapData = async () => {
    setLoading(true);
    setError(false);
    try {
      const requestPayload = {
        profile_type: profileTypeEnum.tradeLanes,
        _id: tradeLaneId,
        category: profileCategory,
        sub_category: activeTabId,
        map_type: profileCategory,
        slug: tradeLaneName,
      };
      if (!tradeLaneCategory || tradeLaneCategory !== profileCategory) {
        dispatch(
          profileMapDataHandler({
            loading: true,
          })
        );
      }

      if (tradeLaneCaching[activeTabId][profileCategory]) {
        const { mapData, imageData } = tradeLaneCaching[activeTabId][profileCategory];
        let mapDataClone = JSON.parse(JSON.stringify(mapData));
        let imageDataClone = JSON.parse(JSON.stringify(imageData));
        setMapData(mapDataClone);
        setImageData(imageDataClone);
        setLoading(false);
        dispatch(profileMapDataHandler(tradeLaneCaching?.profileDetails));
        dispatch(tradeLaneCategoryHandler(profileCategory));
      } else {
        const res = await axios.post(profileTypeEndPoint.logistics.getMapData, requestPayload);
        const { data, status } = res.data;

        if (status && status.toLowerCase() === "success") {
          // create map data for ports ,companies and lanes.
          const { mapData, imageData } = getTradeLanesChartData(data[profileCategory]);
          let profileObj = {};
          setMapData(mapData);
          setImageData(imageData);
          const { nvocc_count, vocc_count, vessel_count, importers_count, exporters_count, freight_forwarder_count, company_id, slug, elastic_data_id } = data;
          if (!tradeLaneCategory || tradeLaneCategory !== profileCategory) {
            profileObj = {
              nvoccCount: nvocc_count ?? 0,
              voccCount: vocc_count ?? 0,
              vesselCount: vessel_count ?? 0,
              importersCount: importers_count ?? 0,
              exportersCount: exporters_count ?? 0,
              freightForwarderCount: freight_forwarder_count ?? 0,
              loading: false,
              companyId: company_id ?? "",
              companyName: slug ?? "",
              elastic_data_id: elastic_data_id ?? "",
            };
            dispatch(profileMapDataHandler(profileObj));
          }
          let tradeLaneCachingClone = JSON.parse(JSON.stringify(tradeLaneCaching));
          tradeLaneCachingClone[activeTabId][profileCategory] = { mapData: mapData ? JSON.parse(JSON.stringify(mapData)) : [], imageData: imageData ? JSON.parse(JSON.stringify(imageData)) : [] };
          if (Object.keys(profileObj)?.length) {
            tradeLaneCachingClone.profileDetails = JSON.parse(JSON.stringify(profileObj));
          }
          dispatch(tradeLaneCachingHandler(tradeLaneCachingClone));
        } else {
          throw new Error("");
        }
      }
    } catch (error) {
      setError(true);
      toast.error("Something went wrong. Please try again later !!!");
    } finally {
      setLoading(false);
    }
  };

  //useLayoutEffect - need error and loading states before rendering due to tab switch
  useLayoutEffect(() => {
    fetchMapData();
  }, []);

  useLayoutEffect(() => {
    setShipmentDistributionLoader(true);
    if (tradeLaneCaching.shipmentDistribution) {
      let tradeLaneCachingClone = JSON.parse(JSON.stringify(tradeLaneCaching));
      setShipmentDistribution(tradeLaneCachingClone.shipmentDistribution);
      setShipmentDistributionLoader(false);
    } else if (activeTabId) {
      fetchShipmentData();
    }
  }, [activeTabId]);

  const fetchShipmentData = async () => {
    try {
      const requestPayload = {
        profile_type: profileTypeEnum.tradeLanes,
        _id: tradeLaneId,
        category: profileCategory,
        sub_category: activeTabId,
        slug: tradeLaneName,
      };

      const res = await axios.post(profileTypeEndPoint.getShipmentDistribution, requestPayload);
      const { data } = res.data;
      if (data && Array.isArray(data) && data.length > 0) {
        setShipmentDistribution(data);
        let tradeLaneCachingClone = JSON.parse(JSON.stringify(tradeLaneCaching));
        tradeLaneCachingClone.shipmentDistribution = data;
        dispatch(tradeLaneCachingHandler(tradeLaneCachingClone));
      }
    } catch (err) {
      setError(true);
      toast.error("Something went wrong. Please try again later !!!");
    } finally {
      setShipmentDistributionLoader(false);
    }
  };

  const viewShipmentsEvent = () => {
    trademoTrackingEvents(getEventName(profileTypeEnum.tradeLanes, tradeLaneEvents.viewShipments), generateTrackingObject(dataSource, pathname, getUserActivePlan()));
  };

  return error ? (
    <div className={styles.noDataContainer}>
      <NoSearchDataComponent content={"Unable to fetch the data at current moment."} />
    </div>
  ) : (
    <div className={styles.portPairsContainer}>
      <div className={styles.renderMapContainer}>
        <LanesChart loading={loading} error={error} mapData={mapData} imageData={imageData} zoomLevel={0} />
      </div>
      <ShipmentDistribution shipmentDistribution={shipmentDistribution} shipmentDistributionLoader={shipmentDistributionLoader} profileType={profileTypeEnum.tradeLanes} profileId={tradeLaneId} category={profileCategory} subCategory={"overview"} profileName={tradeLaneName} viewShipmentsEvent={viewShipmentsEvent} profileRedirectionId={profileRedirectionId} />
    </div>
  );
};

export default PortPairs;
